import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface DataPoint {
  date: string;
  balance: number;
  accountName?: string;
}

interface DataSeries {
  accountName: string;
  data: DataPoint[];
  color: string;
}

interface LineChartProps {
  data: DataPoint[];
  series?: DataSeries[];
  height?: number;
}

function LineChart({ data, series, height = 300 }: LineChartProps): JSX.Element | null {
  const { i18n } = useTranslation();
  const [hoveredPoint, setHoveredPoint] = useState<DataPoint | null>(null);
  const [hoveredSeries, setHoveredSeries] = useState<DataSeries[]>([]);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [dimensions, setDimensions] = useState({ width: 800, height });
  const containerRef = useRef<HTMLDivElement>(null);

  // Update dimensions on resize
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        setDimensions({ width: Math.max(containerWidth, 400), height });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, [height]);

  // Use series data if available, otherwise fall back to single data array
  const chartData = series && series.length > 0 ? series[0].data : data;
  if (!chartData || chartData.length === 0) return null;

  // Calculate dimensions
  const padding = { top: 30, right: 50, bottom: 50, left: 80 };
  const chartWidth = dimensions.width - padding.left - padding.right;
  const chartHeight = dimensions.height - padding.top - padding.bottom;

  // Find min/max values for scaling
  let allValues: number[] = [];
  if (series && series.length > 0) {
    // Multiple series - get all values from all series
    allValues = series.flatMap(s => s.data.map(d => d.balance));
  } else {
    // Single data array
    allValues = chartData.map((d) => d.balance);
  }
  const minValue = Math.min(...allValues);
  const maxValue = Math.max(...allValues);
  const valueRange = Math.max(0, maxValue - minValue);
  const paddingValue = valueRange === 0 ? Math.max(1, maxValue || 1) * 0.1 : valueRange * 0.1;

  // Scale functions include padding so consumers don't need to add it repeatedly
  const scaleX = (index: number) => padding.left + (index / (chartData.length - 1)) * chartWidth;
  const scaleY = (value: number) => {
    const denom = valueRange + paddingValue * 2;
    const safeDenom = denom === 0 ? 1 : denom;
    return (
      padding.top + chartHeight - ((value - minValue + paddingValue) / safeDenom) * chartHeight
    );
  };

  // Generate path strings for each series
  const generatePath = (dataPoints: DataPoint[]) => {
    return dataPoints
      .map((d, i) => `${i === 0 ? 'M' : 'L'} ${scaleX(i)} ${scaleY(d.balance)}`)
      .join(' ');
  };

  // Generate grid lines
  const gridLines = [];
  for (let i = 0; i <= 5; i += 1) {
    const y = padding.top + (i / 5) * chartHeight;
    const value = maxValue - (i / 5) * valueRange;
    gridLines.push(
      <g key={i}>
        <line
          x1={padding.left}
          y1={y}
          x2={padding.left + chartWidth}
          y2={y}
          stroke={i === 0 || i === 5 ? '#e5e7eb' : '#f9fafb'}
          strokeWidth={i === 0 || i === 5 ? 1 : 0.5}
        />
        <text
          x={padding.left - 15}
          y={y + 5}
          textAnchor="end"
          fontSize="11"
          fill="#9ca3af"
          fontWeight="500"
        >
          €{Math.round(value).toLocaleString()}
        </text>
      </g>
    );
  }

  // Generate x-axis labels
  const xAxisLabels = [];
  const labelInterval = Math.max(1, Math.floor(chartData.length / 8));
  for (let i = 0; i < chartData.length; i += labelInterval) {
    const x = scaleX(i);
    xAxisLabels.push(
      <text
        key={i}
        x={x}
        y={dimensions.height - 15}
        textAnchor="middle"
        fontSize="11"
        fill="#9ca3af"
        fontWeight="500"
      >
        {new Date(chartData[i].date).toLocaleDateString(i18n.language === 'nl' ? 'nl-NL' : 'en-US', { month: 'short', day: 'numeric' })}
      </text>
    );
  }

  // Handle mouse events
  const handleMouseMove = (event: React.MouseEvent<SVGSVGElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    setMousePosition({ x, y });

    // Find closest data point
    const chartX = x - padding.left;
    if (chartX >= 0 && chartX <= chartWidth) {
      const index = Math.round((chartX / chartWidth) * (chartData.length - 1));
      if (index >= 0 && index < chartData.length) {
        setHoveredPoint(chartData[index]);
        
        // If we have multiple series, find the corresponding points in all series
        if (series && series.length > 0) {
          const seriesAtPoint = series.map(s => ({
            ...s,
            data: [s.data[index]] // Get the point at this index
          }));
          setHoveredSeries(seriesAtPoint);
        }
      }
    }
  };

  const handleMouseLeave = () => {
    setHoveredPoint(null);
    setHoveredSeries([]);
  };

  return (
    <div ref={containerRef} className="w-full relative">
      <svg
        width={dimensions.width}
        height={dimensions.height}
        className="w-full h-full"
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        {/* Grid lines */}
        {gridLines}

        {/* X-axis */}
        <line
          x1={padding.left}
          y1={dimensions.height - padding.bottom}
          x2={padding.left + chartWidth}
          y2={dimensions.height - padding.bottom}
          stroke="#e5e7eb"
          strokeWidth={1}
        />

        {/* Y-axis */}
        <line
          x1={padding.left}
          y1={padding.top}
          x2={padding.left}
          y2={dimensions.height - padding.bottom}
          stroke="#e5e7eb"
          strokeWidth={1}
        />

        {/* Multiple series lines */}
        {series && series.length > 0 ? (
          series.map((s, index) => (
            <path
              key={s.accountName}
              d={generatePath(s.data)}
              fill="none"
              stroke={s.color}
              strokeWidth={2.5}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          ))
        ) : (
          <path
            d={generatePath(chartData)}
            fill="none"
            stroke="#2563eb"
            strokeWidth={2.5}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        )}

        {/* Hover indicator line */}
        {hoveredPoint && (
          <line
            x1={scaleX(chartData.indexOf(hoveredPoint))}
            y1={padding.top}
            x2={scaleX(chartData.indexOf(hoveredPoint))}
            y2={dimensions.height - padding.bottom}
            stroke="#d1d5db"
            strokeWidth={1}
            strokeDasharray="3,3"
            opacity={0.8}
          />
        )}

        {/* Hover data points for all series */}
        {hoveredSeries.length > 0 && hoveredPoint && hoveredSeries.map((s, index) => {
          const point = s.data[0];
          if (!point) return null;
          return (
            <circle
              key={s.accountName}
              cx={scaleX(chartData.indexOf(hoveredPoint))}
              cy={scaleY(point.balance)}
              r={5}
              fill={s.color}
              stroke="white"
              strokeWidth={3}
            />
          );
        })}
        
        {/* Fallback for single data point */}
        {hoveredPoint && (!series || series.length === 0) && (
          <circle
            cx={scaleX(chartData.indexOf(hoveredPoint))}
            cy={scaleY(hoveredPoint.balance)}
            r={5}
            fill="#2563eb"
            stroke="white"
            strokeWidth={3}
          />
        )}

        {/* X-axis labels */}
        {xAxisLabels}
      </svg>

      {/* Tooltip */}
      {(hoveredPoint || hoveredSeries.length > 0) && (
        <div
          className="absolute bg-white border border-gray-200 rounded-xl shadow-xl p-4 pointer-events-none z-10 min-w-[180px]"
          style={{
            left: Math.min(mousePosition.x + 15, dimensions.width - 200),
            top: Math.max(mousePosition.y - 90, 10),
          }}
        >
          <div className="text-sm font-semibold text-gray-900 mb-3">
            {new Date((hoveredPoint || hoveredSeries[0]?.data[0])?.date || '').toLocaleDateString(i18n.language === 'nl' ? 'nl-NL' : 'en-US', {
              weekday: 'short',
              year: 'numeric',
              month: 'short',
              day: 'numeric',
            })}
          </div>
          <div className="space-y-2">
            {hoveredSeries.length > 0 ? (
              // Show multiple series
              hoveredSeries.map((s) => {
                const point = s.data[0];
                if (!point) return null;
                return (
                  <div key={s.accountName} className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: s.color }}
                      />
                      <span className="text-sm text-gray-600">{s.accountName}</span>
                    </div>
                    <span className="text-sm font-semibold text-gray-900">
                      €{point.balance.toLocaleString()}
                    </span>
                  </div>
                );
              })
            ) : (
              // Show single data point
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 rounded-full bg-blue-600" />
                  <span className="text-sm text-gray-600">Total Balance</span>
                </div>
                <span className="text-sm font-semibold text-gray-900">
                  €{hoveredPoint?.balance.toLocaleString()}
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LineChart;
